/*
 * Jula Bloom custom behaviour
 * ------------------------------------------------------------
 * Kept in one prefixed file so the native theme scripts stay untouched.
 */

/*
 * KK-862: Personal note
 * Saves the handwritten note request to BOTH the cart note and a cart attribute.
 *
 * The attribute alone was the original design, so the value would arrive on the
 * order in its own labelled field. It does, and the admin shows it under
 * Additional details. The packing slip does not: its Liquid context exposes no
 * cart attributes at all. Proven on order #1001 by printing every candidate:
 *
 *   attributes=null  note_attributes=null
 *   order.attributes=null  order.note_attributes=null
 *
 * order.note is the only channel the packing slip can read, and whoever writes
 * the card reads the packing slip. So the value goes to both: note to reach
 * fulfilment, attribute to keep the labelled display in the admin.
 *
 * The theme's own cart note field is switched off in templates/cart.json, so
 * nothing else writes to note and there is one box on the page rather than two.
 */
if (!customElements.get('jb-personal-note')) {
  customElements.define(
    'jb-personal-note',
    class JbPersonalNote extends HTMLElement {
      constructor() {
        super();

        this.addEventListener(
          'change',
          debounce((event) => {
            const value = event.target.value;
            const body = JSON.stringify({
              note: value,
              attributes: { 'Personal note': value },
            });
            fetch(`${theme.routes.cart_update_url}`, { ...fetchConfig(), ...{ body } });
          }, 300)
        );
      }
    }
  );
}

/*
 * KK-1205: Lead capture modal
 *
 * Markup: snippets/jb-lead-form-modal.liquid. Two pieces live here.
 *
 * 1. <jb-lead-form>, which posts the lead to the Klaviyo Client Subscriptions
 *    API. The request shape is the one already proven in production on this
 *    store by snippets/klaviyo-form.liquid (KK-222) in the footer: the
 *    subscriptions object sits INSIDE the profile, the target list is a
 *    relationship, and 202 counts as success.
 *
 *    Two things are added on top of that footer form. custom_source carries the
 *    page the lead came from, which is Caroline's ask that "people who click on
 *    the CTA button for this site" are "tagged with Business Services", and the
 *    same value is repeated as a Lead Source profile property so the tag is
 *    visible on the profile and not only on the subscription record.
 *
 *    The phone number goes into properties as Phone, deliberately NOT into the
 *    profile phone_number field. Klaviyo validates phone_number as E.164 and
 *    rejects the whole request when it fails, which would lose the lead over a
 *    formatting detail. Company, Event type and Brief description are custom
 *    properties for the same reason: they are not standard profile fields.
 *
 *    The form is NOT marked novalidate, unlike the footer one. The browser's own
 *    validation blocks an empty required field and a malformed email before this
 *    handler ever runs.
 *
 * 2. One delegated click listener that opens the modal. Any anchor whose href
 *    contains #jb-lead- resolves to the modal element with that id, which is the
 *    trigger contract agreed for this checkpoint:
 *      Business Services  -> #jb-lead-business-services
 *      Events             -> #jb-lead-events
 *      Corporate Gifting  -> #jb-lead-corporate-gifting
 *      Custom Bouquets    -> #jb-lead-custom-bouquets
 *    The default is only prevented once the modal is found and is a real
 *    <modal-dialog>, so a CTA on a page without the snippet stays a harmless in
 *    page jump instead of a dead button.
 *
 * The whole block is guarded on customElements.get, not just the define, because
 * the snippet loads this file with its own script tag and a page could carry
 * more than one modal. Without the guard the click listener would be attached
 * once per render and every CTA would open the modal twice.
 */
if (!customElements.get('jb-lead-form')) {
  customElements.define(
    'jb-lead-form',
    class JbLeadForm extends HTMLElement {
      connectedCallback() {
        if (this.bound) return;
        this.bound = true;

        this.form = this.querySelector('form');
        this.honeypot = this.querySelector('input[name="company_website"]');
        this.errorEl = this.querySelector('.jb-lead-form__error');
        this.successEl = this.querySelector('.jb-lead-form__success');

        if (this.form) this.form.addEventListener('submit', this.onSubmit.bind(this));
      }

      fieldValue(name) {
        const field = this.querySelector('[name="' + name + '"]');
        return field ? (field.value || '').trim() : '';
      }

      async onSubmit(event) {
        event.preventDefault();
        if (this.errorEl) this.errorEl.hidden = true;
        if (this.honeypot && this.honeypot.value) return; // bot

        const email = this.fieldValue('email');
        if (!email) return;

        const source = this.dataset.source || '';
        const properties = {};

        // Klaviyo profile property names, one per form field. These are the
        // names Caroline will see when she builds a segment, so they read as
        // English rather than as field keys.
        //
        // Client Type is the one that earns its place: it carries her "Who is
        // this for" answer, Personal or Business, which is the split she asked
        // for on Custom Bouquets. Lead Source already says which page the lead
        // came from, so the two together give her page and intent.
        const propertyMap = {
          company: 'Company',
          phone: 'Phone',
          occasion: 'Occasion',
          recipients: 'Number of recipients',
          space: 'About their space',
          audience: 'Client Type',
          delivery_date: 'Preferred delivery date',
          event_type: 'Event type',
          description: 'Brief description',
        };

        Object.keys(propertyMap).forEach((name) => {
          const value = this.fieldValue(name);
          if (value) properties[propertyMap[name]] = value;
        });
        if (source) properties['Lead Source'] = source;

        const profileAttributes = {
          email: email,
          subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } },
        };

        const firstName = this.fieldValue('first_name');
        const lastName = this.fieldValue('last_name');
        if (firstName) profileAttributes.first_name = firstName;
        if (lastName) profileAttributes.last_name = lastName;
        if (Object.keys(properties).length) profileAttributes.properties = properties;

        const attributes = {
          profile: { data: { type: 'profile', attributes: profileAttributes } },
        };
        if (source) attributes.custom_source = source;

        const payload = {
          data: {
            type: 'subscription',
            attributes: attributes,
            relationships: { list: { data: { type: 'list', id: this.dataset.list } } },
          },
        };

        this.setLoading(true);
        try {
          const res = await fetch(
            'https://a.klaviyo.com/client/subscriptions?company_id=' + encodeURIComponent(this.dataset.company),
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', revision: this.dataset.revision || '2026-04-15' },
              body: JSON.stringify(payload),
            }
          );
          if (res.ok || res.status === 202) {
            this.trackInquiry(email, firstName, lastName, properties);
            this.showSuccess();
          } else {
            this.showError();
          }
        } catch (err) {
          this.showError();
        } finally {
          this.setLoading(false);
        }
      }

      // KK-1876: the subscription alone cannot tell Caroline about every
      // inquiry. Klaviyo records "Subscribed to List" only the first time an
      // address joins the list, and a repeat inquiry just overwrites the profile
      // properties, so a flow triggered by the list stays silent from a person's
      // second inquiry on (tested 28 September 2026). An event is recorded on
      // every submit and carries this inquiry's own answers, so her "Website
      // Inquiry Alert" flow triggers on it. Sent only once the lead is saved and
      // never awaited: a failed event costs the alert, not the lead, and the
      // visitor sees the success message either way.
      trackInquiry(email, firstName, lastName, properties) {
        if (!this.dataset.event) return;

        const eventProperties = Object.assign({ 'First name': firstName, 'Last name': lastName, Email: email }, properties);
        const payload = {
          data: {
            type: 'event',
            attributes: {
              properties: eventProperties,
              metric: { data: { type: 'metric', attributes: { name: this.dataset.event } } },
              profile: { data: { type: 'profile', attributes: { email: email } } },
            },
          },
        };

        fetch('https://a.klaviyo.com/client/events?company_id=' + encodeURIComponent(this.dataset.company), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', revision: this.dataset.revision || '2026-04-15' },
          body: JSON.stringify(payload),
        }).catch(() => {});
      }

      setLoading(state) {
        const button = this.querySelector('button[type="submit"]');
        if (!button) return;
        button.toggleAttribute('disabled', state);

        // Disabling alone greys the button out silently. On a slow connection
        // that reads as a dead button, and the visitor clicks again or closes
        // the modal and the lead is lost. Say what is happening, and put the
        // original label back if the request fails so they can retry.
        if (state) {
          if (!this.submitLabel) this.submitLabel = button.textContent;
          button.textContent = 'Sending...';
        } else if (this.submitLabel) {
          button.textContent = this.submitLabel;
        }
      }

      showSuccess() {
        if (this.form) {
          // Disabled before hidden, on purpose. getFocusableElements() in
          // global.js filters on :enabled, not on visibility, so a merely
          // hidden control still counts as the last element of the rebuilt
          // focus trap and Tab would walk straight out of the modal, taking
          // the Escape close with it. Disabling also blocks a resubmit.
          this.form.querySelectorAll('input, select, textarea, button').forEach((el) => {
            el.disabled = true;
          });
          this.form.hidden = true;
        }
        if (!this.successEl) return;
        this.successEl.hidden = false;

        // The focus trap was built from the elements present when the modal
        // opened, and the form that held most of them has just been disabled
        // and hidden. Rebuild it, but hand focus to the CLOSE BUTTON rather
        // than to the confirmation paragraph.
        //
        // trapFocus in global.js only arms its keydown handler from its own
        // focusin handler, and only when the focused node is the container,
        // `first` or `last`. The confirmation <p> carries tabindex="-1", so it
        // is none of those: focusing it leaves the trap unarmed, and since the
        // <p> is the last node in the dialog the next Tab walks straight out of
        // the modal, taking the Escape binding with it. The close button is the
        // only remaining focusable control, so it is both `first` and `last`,
        // and focusing it arms the trap properly.
        const modal = this.closest('modal-dialog');
        const closeButton = modal ? modal.querySelector('.jb-lead-modal__close') : null;
        if (modal && closeButton && typeof window.trapFocus === 'function') {
          window.trapFocus(modal, closeButton);
        } else if (closeButton) {
          closeButton.focus();
        } else {
          this.successEl.focus();
        }
      }

      showError() {
        if (!this.errorEl) return;
        this.errorEl.textContent = 'Sorry, something went wrong. Please try again.';
        this.errorEl.hidden = false;
      }
    }
  );

  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const trigger = target ? target.closest('a[href*="#jb-lead-"]') : null;
    if (!trigger) return;

    const href = trigger.getAttribute('href') || '';
    const hashIndex = href.indexOf('#jb-lead-');
    if (hashIndex === -1) return;

    const modal = document.getElementById(href.slice(hashIndex + 1));
    if (!modal || typeof modal.show !== 'function') return;

    event.preventDefault();
    modal.show(trigger);
  });
}
